'use client';

import './feature-article-card.scss';

import Link from 'next/link';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import {
  Prominence,
  Sizes,
  Text,
  Variants,
} from '@sola/ark-core/atoms/text/text.js';
import { SearchResultProps } from '@sola/skippy-search-sdk/lib/schema.js';
import { titleCase } from '@sola/utils/string.js';

import { Image } from '../../atoms/image/image';
import { FeatureBase } from '../feature-base/feature-base';

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
} as const;

type FeaturedArticleCardProps = SearchResultProps & {
  seoLevel: number;
};

export const FeaturedArticleCard = ({
  seoLevel,
  ...article
}: FeaturedArticleCardProps) => {
  const { t } = useTranslation(['articles', 'geo']);

  const { title, summary } = article;
  const href = article.path || '#';

  const publishedDate = useMemo(() => {
    if (!article.publishDate) {
      return null;
    }
    return new Date(article.publishDate).toLocaleDateString(
      'en-US',
      DATE_FORMAT_OPTIONS,
    );
  }, [article.publishDate]);

  const firstContributor = useMemo(
    () => article.contributors?.[0] || null,
    [article.contributors],
  );

  const hasTags = !!(
    article.taxonomyArticleType ||
    article.taxonomyProgramCountry?.length ||
    article.taxonomyArticleTheme?.length ||
    article.taxonomyTopic?.length
  );

  const { Comp, CompProps } = useMemo(() => {
    const isClickable = !!href && href !== '#';
    return isClickable
      ? { Comp: Link as React.ElementType, CompProps: { href } }
      : { Comp: 'div' as React.ElementType, CompProps: {} };
  }, [href]);

  return (
    <Comp
      {...CompProps}
      className="FeaturedArticleCard w-full max-w-xl mx-auto aspect-ratio-1 @md:aspect-ratio-16-9 @lg:aspect-ratio-21-9"
    >
      <div className="FeaturedArticleCard__media">
        <Image
          image={article.media?.image}
          className="FeaturedArticleCard__image"
          hasPlaceholder
        />
        <div className="FeaturedArticleCard__media-overlay" />
      </div>

      <div className="FeaturedArticleCard__content">
        <FeatureBase
          seoLevel={seoLevel}
          title={title}
          body={<p>{summary}</p>}
          className="max-w-md @lg:pr-roomy-lg"
          inline
        />

        <div className="FeaturedArticleCard__meta flex items-start justify-between gap-comfy-xl flex-wrap pt-comfy-lg">
          {firstContributor && (
            <div className="flex flex-col gap-snug-lg">
              <Text
                variant={Variants.Label}
                size={Sizes.Medium}
                prominence={Prominence.Potent}
              >
                {t('articles:writtenBy')}
              </Text>
              <div className="flex flex-row items-center gap-snup-lg">
                {firstContributor.image?.url && (
                  <div className="pr-comfy-xs">
                    <Image
                      image={{
                        url: firstContributor.image?.url,
                        alt:
                          firstContributor.image?.alt || firstContributor.name,
                      }}
                      className="FeaturedArticleCard__avatar w-8 h-8 rounded-full object-cover"
                    />
                  </div>
                )}
                <Text
                  variant={Variants.Label}
                  size={Sizes.Medium}
                  prominence={Prominence.Moderate}
                >
                  {firstContributor.name}
                </Text>
              </div>
            </div>
          )}

          {publishedDate && (
            <div className="flex flex-col gap-snug-lg">
              <Text
                variant={Variants.Label}
                size={Sizes.Medium}
                prominence={Prominence.Potent}
              >
                {t('articles:publishedOn')}
              </Text>
              <div className="mt-snug-lg">
                <Text
                  variant={Variants.Label}
                  size={Sizes.Medium}
                  prominence={Prominence.Moderate}
                >
                  {publishedDate}
                </Text>
              </div>
            </div>
          )}

          {hasTags && (
            <div className="flex flex-col gap-snug-lg sm:ml-auto text-left">
              <Text
                variant={Variants.Label}
                size={Sizes.Medium}
                prominence={Prominence.Potent}
              >
                {t('articles:tags')}
              </Text>
              <div className="flex flex-wrap justify-end gap-snug-xl mt-snug-lg">
                {article.taxonomyArticleType?.[0] && (
                  <Badge
                    key="type"
                    label={titleCase(article.taxonomyArticleType)}
                  />
                )}

                {article.taxonomyProgramCountry?.[0] && (
                  <Badge
                    key="country"
                    label={titleCase(
                      t(
                        `geo:countries.${
                          article.taxonomyProgramCountry[0]
                        }.nameS`,
                      ),
                    )}
                  />
                )}

                {article.taxonomyArticleTheme?.[0] && (
                  <Badge
                    key="theme"
                    label={titleCase(article.taxonomyArticleTheme[0])}
                  />
                )}

                {article.taxonomyTopic?.[0] && (
                  <Badge
                    key="topic"
                    label={titleCase(article.taxonomyTopic[0])}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Comp>
  );
};
